import React, {useCallback, useEffect, useRef, useState} from "react";
import SplitterArrow from "../../assets/splitter_arrows.svg";

type Bound = number | `${number}%` | `${number}vw` | `${number}px`;

type SplitPaneProps = {
    left: React.ReactNode;
    right: React.ReactNode;
    storageKey: string;
    initialLeftSize?: number | string;
    minLeft?: Bound;
    maxLeft?: Bound;
    className?: string;
};

export const SplitPane: React.FC<SplitPaneProps> = ({
                                                        left,
                                                        right,
                                                        storageKey,
                                                        initialLeftSize = 460,
                                                        minLeft = 380,
                                                        maxLeft = 980,
                                                        className = "",
                                                    }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [leftSize, setLeftSize] = useState<number>(460);
    useEffect(() => {
        const saved = storageKey ? Number(localStorage.getItem(storageKey) || "") : NaN;
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const base = !Number.isNaN(saved)
                ? saved
                : initialToPx(initialLeftSize, rect.width);
            setLeftSize(clamp(base, rect.width));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dragRef = useRef<{ dragging: boolean; frame?: number }>({dragging: false});

    const clamp = useCallback((v: number, containerW: number) => {
        const minPx = toPx(minLeft, containerW) ?? 0;
        const maxPx = toPx(maxLeft, containerW) ?? containerW; // default: full width
        return Math.min(Math.max(v, minPx), maxPx);
    }, [minLeft, maxLeft]);

    useEffect(() => {
        if (!wrapperRef.current) return;
        const el = wrapperRef.current;

        const recompute = () => {
            const rect = el.getBoundingClientRect();
            setLeftSize(prev => clamp(prev, rect.width));
        };

        // Prefer ResizeObserver for container changes
        let ro: ResizeObserver | null = null;
        if ("ResizeObserver" in window) {
            ro = new ResizeObserver(recompute);
            ro.observe(el);
        }
        // And also on viewport resize (for `vw` bounds)
        window.addEventListener("resize", recompute);

        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", recompute);
        };
    }, [clamp]);

    const startDrag = (e: React.MouseEvent) => {
        e.preventDefault();
        dragRef.current.dragging = true;
        document.body.classList.add("select-none", "cursor-col-resize");
    };

    function toPx(bound: Bound | undefined, containerW: number): number | undefined {
        if (bound == null) return undefined;
        if (typeof bound === "number") return bound;                 // px
        if (bound.endsWith("px")) return parseFloat(bound);
        if (bound.endsWith("%")) return (parseFloat(bound) / 100) * containerW;
        if (bound.endsWith("vw")) return (parseFloat(bound) / 100) * window.innerWidth;
        // fallback: treat as px
        return parseFloat(bound as string);
    }

    function initialToPx(init: number | string, containerW: number) {
        if (typeof init === "number") return init;
        if (init.endsWith("%")) return (parseFloat(init) / 100) * containerW;
        if (init.endsWith("vw")) return (parseFloat(init) / 100) * window.innerWidth;
        if (init.endsWith("px")) return parseFloat(init);
        return parseFloat(init) || 460;
    }

    const stopDrag = useCallback(() => {
        if (!dragRef.current.dragging) return;
        dragRef.current.dragging = false;
        document.body.classList.remove("select-none", "cursor-col-resize");
        if (storageKey) localStorage.setItem(storageKey, String(leftSize));
    }, [leftSize, storageKey]);

    const onMove = useCallback((clientX: number) => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const next = clamp(clientX - rect.left, rect.width);
        setLeftSize(next);
    }, [clamp]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!dragRef.current.dragging) return;
            if (dragRef.current.frame) cancelAnimationFrame(dragRef.current.frame);
            dragRef.current.frame = requestAnimationFrame(() => onMove(e.clientX));
        };
        const onMouseUp = () => stopDrag();

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("mouseleave", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("mouseleave", onMouseUp);
        };
    }, [onMove, stopDrag]);

    const handleDoubleClick = () => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();

        // resolve initialLeftSize into px
        let resetPx: number;
        if (typeof initialLeftSize === "number") {
            resetPx = initialLeftSize;
        } else {
            resetPx = toPx(initialLeftSize as any, rect.width) ?? 460;
        }

        const clamped = clamp(resetPx, rect.width);
        setLeftSize(clamped);

        if (storageKey) {
            localStorage.setItem(storageKey, String(clamped));
        }
    };

    return (
        <div ref={wrapperRef} className={`w-full h-full flex overflow-hidden min-w-0 ${className}`}>
            <div style={{width: leftSize}} className="h-full shrink-0 min-w-0 overflow-auto">
                {left}
            </div>

            <div
                onMouseDown={startDrag}
                onDoubleClick={handleDoubleClick}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize panels"
                tabIndex={0}
                className="w-[20px] shrink-0 cursor-col-resize relative bg-transparent group"
            >
                <img alt="" src={SplitterArrow} className=" " />
                <svg
                    className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full w-[2px] pointer-events-none pt-5"
                    viewBox="0 0 1 100"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id="split-line" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="black" stopOpacity="1"/>
                            <stop offset="50%" stopColor="black" stopOpacity="1"/>
                            <stop offset="100%" stopColor="black" stopOpacity="1"/>
                        </linearGradient>
                    </defs>
                    {/* the line */}
                    <rect x="0" y="0" width="1" height="100" fill="#979797"/>
                </svg>
            </div>
            <div className="flex-1 min-w-0 h-full overflow-auto items-stretch">
                {right}
            </div>
        </div>
    );
};
